import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Modal, Input, Select, ImageUpload, ConfirmDialog, Spinner } from '../../components/common';
import { galleryService } from '../../services/galleryService';
import { useModal } from '../../hooks';
import './GalleryPage.css';

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  
  const uploadModal = useModal();
  const deleteModal = useModal();
  const [uploadForm, setUploadForm] = useState({ title: '', category: '', images: [] });
  const [uploading, setUploading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await galleryService.getAll({
        search,
        category: categoryFilter
      });
      setImages(response.data.images);
    } catch (error) {
      toast.error('Failed to fetch gallery images');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter]);

  const fetchCategories = async () => {
    try {
      const response = await galleryService.getCategories();
      setCategories(response.data.map(cat => ({ value: cat, label: cat })));
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  useEffect(() => {
    fetchImages();
    fetchCategories();
  }, [fetchImages]);

  const handleUpload = async () => {
    if (!uploadForm.images.length) {
      toast.error('Please select images to upload');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('category', uploadForm.category);
      uploadForm.images.forEach(img => {
        formData.append('images', img.file);
      });
      
      await galleryService.upload(formData);
      toast.success('Images uploaded successfully');
      uploadModal.close();
      setUploadForm({ title: '', category: '', images: [] });
      fetchImages();
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await galleryService.delete(deleteModal.data);
      toast.success('Image deleted successfully');
      fetchImages();
    } catch (error) {
      toast.error('Failed to delete image');
    } finally {
      setDeleteLoading(false);
      deleteModal.close();
    }
  };

  return (
    <div className="gallery-page">
      <Card
        title="Gallery"
        subtitle={`${images.length} images`}
        action={
          <Button icon={<FiPlus />} onClick={() => uploadModal.open()}>
            Upload Images
          </Button>
        }
      >
        <div className="gallery-toolbar">
          <div className="gallery-search">
            <input
              type="text"
              placeholder="Search images..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select
            name="category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            options={categories}
            placeholder="All Categories"
          />
        </div>

        {loading ? (
          <div className="gallery-loading">
            <Spinner size="large" />
          </div>
        ) : images.length === 0 ? (
          <div className="gallery-empty">
            <p>No images found</p>
            <Button icon={<FiPlus />} onClick={() => uploadModal.open()}>
              Upload Images
            </Button>
          </div>
        ) : (
          <div className="gallery-grid">
            {images.map((image) => (
              <div key={image.id} className="gallery-item">
                <img src={image.imageUrl} alt={image.title} />
                <div className="gallery-item-overlay">
                  <span className="gallery-item-title">{image.title}</span>
                  <span className="gallery-item-category">{image.category}</span>
                  <div className="gallery-item-actions">
                    <button 
                      className="gallery-action-btn delete"
                      onClick={() => deleteModal.open(image.id)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Upload Modal */}
      <Modal
        isOpen={uploadModal.isOpen}
        onClose={uploadModal.close}
        title="Upload Images"
        size="medium"
        footer={
          <>
            <Button variant="ghost" onClick={uploadModal.close}>
              Cancel
            </Button>
            <Button onClick={handleUpload} loading={uploading}>
              Upload
            </Button>
          </>
        }
      >
        <div className="upload-form">
          <Input
            className="form-text-input"
            name="title"
            label="Title Prefix"
            placeholder="Enter image title"
            value={uploadForm.title}
            onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
          />
          <Select
            name="category"
            label="Category"
            value={uploadForm.category}
            onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
            options={categories}
            placeholder="Select category"
          />
          <ImageUpload
            value={uploadForm.images}
            onChange={(images) => setUploadForm({ ...uploadForm, images })}
            multiple
            maxFiles={10}
            label="Select Images"
          />
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        title="Delete Image"
        message="Are you sure you want to delete this image? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default GalleryPage;
